import { useEffect, useId, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { CopyIcon, Mtds } from 'prove-common'
import { ReferenceDeleteDialog } from '../../components/reference-data/ReferenceDeleteDialog'
import { useReferenceTableQuery } from './useReferenceTableQuery'
import styles from '../../components/reference-data/referenceData.module.css'

export type ReferenceColumn<T> = {
  key: string
  label: string
  value: (row: T) => string | number | null | undefined
  copy?: boolean
}

export function CopyReferenceValue({
  value,
  label
}: {
  value: string | number
  label: string
}) {
  const [feedback, setFeedback] = useState('')
  return (
    <Mtds.Flex data-align='center' data-gap='1' data-fixed>
      <span>{value}</span>
      <Mtds.Button
        type='button'
        data-size='sm'
        aria-label={`Kopier ${label}: ${value}`}
        data-tooltip={`Kopier ${label}: ${value}`}
        onClick={async () => {
          setFeedback('')
          try {
            await navigator.clipboard.writeText(String(value))
            setFeedback(`${label} kopiert`)
          } catch {
            setFeedback('Kunne ikke kopiere. Marker og kopier verdien manuelt.')
          }
        }}
      >
        <CopyIcon />
      </Mtds.Button>
      <Mtds.Muted role='status'>{feedback}</Mtds.Muted>
    </Mtds.Flex>
  )
}

type Props<T> = {
  rows: T[]
  title: string
  emptyTitle: string
  columns: ReferenceColumn<T>[]
  name: (row: T) => string
  /** Omit to render a navigation-only table without an actions column. */
  onDelete?: (id: number) => Promise<unknown>
  editForm?: (row: T, close: () => void) => ReactNode
  protectedReason?: (row: T) => string | undefined
  detailPath?: (row: T) => string
  extraActions?: (row: T) => ReactNode
}

export function ReferenceTable<T extends { id: number }>({
  rows,
  title,
  emptyTitle,
  columns,
  name,
  onDelete,
  editForm,
  protectedReason,
  detailPath,
  extraActions
}: Props<T>) {
  const state = useReferenceTableQuery(columns.map(({ key }) => key))
  const id = useId()
  const hasActions = !!onDelete || !!editForm || !!extraActions
  const [deleting, setDeleting] = useState<T | null>(null)
  const [editing, setEditing] = useState<T | null>(null)
  const needle = state.q.trim().toLocaleLowerCase('nb')
  const sortedColumn =
    columns.find(({ key }) => key === state.sort) ?? columns[0]
  const filtered = rows
    .filter((row) =>
      columns.some(({ value }) =>
        String(value(row) ?? '')
          .toLocaleLowerCase('nb')
          .includes(needle)
      )
    )
    .sort((a, b) => {
      const left = sortedColumn.value(a) ?? ''
      const right = sortedColumn.value(b) ?? ''
      const order =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right), 'nb', {
              numeric: true,
              sensitivity: 'base'
            })
      return (order || a.id - b.id) * (state.direction === 'desc' ? -1 : 1)
    })
  const pages = Math.max(1, Math.ceil(filtered.length / state.pageSize))
  const page = Math.min(state.page, pages)
  const offset = (page - 1) * state.pageSize
  const { update, page: requestedPage } = state
  useEffect(() => {
    if (requestedPage !== page) update({ page })
  }, [page, requestedPage, update])

  return (
    <Mtds.Grid data-gap='5'>
      <Mtds.Flex data-align='end' data-items='250'>
        <Mtds.Field
          as='input'
          type='search'
          label={`Søk i ${title.toLocaleLowerCase('nb')}`}
          description='Søk etter navn, ID eller kode.'
          value={state.q}
          onChange={(event) =>
            state.update({ q: event.target.value, page: null })
          }
        />
        <Mtds.Field
          as='select'
          label='Sorter etter'
          value={state.sort}
          options={columns.map(({ key, label }) => ({ value: key, label }))}
          onChange={(event) =>
            state.update({ sort: event.target.value, page: null })
          }
        />
        <Mtds.Field
          as='select'
          label='Sorteringsretning'
          value={state.direction}
          options={[
            { value: 'asc', label: 'Stigende' },
            { value: 'desc', label: 'Synkende' }
          ]}
          onChange={(event) =>
            state.update({ direction: event.target.value, page: null })
          }
        />
      </Mtds.Flex>
      <Mtds.Muted role='status'>
        {filtered.length} av {rows.length} oppføringer
        {filtered.length > state.pageSize
          ? ` · Viser ${offset + 1}–${Math.min(offset + state.pageSize, filtered.length)}`
          : ''}
      </Mtds.Muted>
      {!filtered.length ? (
        <Mtds.Grid>
          <Mtds.Heading as='h2' data-size='sm'>
            {rows.length ? 'Ingen treff' : emptyTitle}
          </Mtds.Heading>
          <p>
            {rows.length
              ? 'Prøv et annet navn, en ID eller en kode.'
              : 'Legg til en oppføring for å komme i gang.'}
          </p>
          {state.q && (
            <Mtds.Button
              data-variant='secondary'
              onClick={() => state.update({ q: null, page: null })}
            >
              Nullstill søk
            </Mtds.Button>
          )}
        </Mtds.Grid>
      ) : (
        <Mtds.Table
          className={styles.table}
          aria-label={title}
          data-mobile='stacked'
          data-mobile-source='self'
          data-align='center'
        >
          <thead>
            <tr>
              {columns.map((column) => (
                <Mtds.Table.ThSortable
                  key={column.key}
                  scope='col'
                  aria-sort={
                    (state.sort === column.key
                      ? state.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none') as 'asc' | 'desc' | 'none'
                  }
                  onClick={() =>
                    state.update({
                      sort: column.key,
                      direction:
                        state.sort === column.key && state.direction === 'asc'
                          ? 'desc'
                          : 'asc',
                      page: null
                    })
                  }
                >
                  {column.label}
                </Mtds.Table.ThSortable>
              ))}
              {hasActions && <th scope='col'>Handlinger</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(offset, offset + state.pageSize).map((row) => {
              const reason = protectedReason?.(row)
              const linkId = `${id}-${row.id}-link`
              return (
                <tr
                  key={row.id}
                  data-clickdelegatefor={detailPath ? linkId : undefined}
                >
                  {columns.map((column, index) => {
                    const value = column.value(row)
                    const content =
                      value == null || value === '' ? (
                        'Ikke angitt'
                      ) : column.copy ? (
                        <CopyReferenceValue
                          value={value}
                          label={column.label}
                        />
                      ) : (
                        value
                      )
                    return index === 0 ? (
                      <th key={column.key} scope='row'>
                        {detailPath ? (
                          <Link id={linkId} to={detailPath(row)}>
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </th>
                    ) : (
                      <td key={column.key}>{content}</td>
                    )
                  })}
                  {hasActions && (
                    <td>
                      <Mtds.Grid data-gap='2'>
                        <Mtds.Flex data-gap='2' data-justify='end'>
                          {extraActions?.(row)}
                          {editForm && (
                            <Mtds.Button
                              data-variant='secondary'
                              disabled={!!reason}
                              aria-describedby={
                                reason ? `${id}-${row.id}-protected` : undefined
                              }
                              aria-label={`Rediger ${name(row)}`}
                              onClick={() => setEditing(row)}
                            >
                              Rediger
                            </Mtds.Button>
                          )}
                          {onDelete && (
                            <Mtds.Button
                              data-variant='secondary'
                              data-color='danger'
                              disabled={!!reason}
                              aria-describedby={
                                reason ? `${id}-${row.id}-protected` : undefined
                              }
                              aria-label={`Slett ${name(row)}`}
                              onClick={() => {
                                setDeleting(row)
                              }}
                            >
                              Slett
                            </Mtds.Button>
                          )}
                        </Mtds.Flex>
                        {reason && (
                          <Mtds.Muted id={`${id}-${row.id}-protected`}>
                            {reason}
                          </Mtds.Muted>
                        )}
                      </Mtds.Grid>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </Mtds.Table>
      )}
      {filtered.length > 25 && (
        <Mtds.Flex data-align='end' data-justify='space-between'>
          <Mtds.Field
            as='select'
            label='Rader per side'
            value={String(state.pageSize)}
            options={['25', '50', '100']}
            onChange={(event) =>
              state.update({ pageSize: event.target.value, page: null })
            }
          />
          {pages > 1 && (
            <Mtds.Pagination
              aria-label='Sidenavigering'
              current={page}
              total={pages}
              show={5}
              props={({ page: next }) => ({
                onClick: () => state.update({ page: next })
              })}
            />
          )}
        </Mtds.Flex>
      )}
      {editing && editForm?.(editing, () => setEditing(null))}
      {deleting && onDelete && (
        <ReferenceDeleteDialog
          className={styles.dialog}
          id={`${id}-delete-dialog`}
          name={name(deleting)}
          onClose={() => setDeleting(null)}
          onDelete={() => onDelete(deleting.id)}
        />
      )}
    </Mtds.Grid>
  )
}
